"use client";

import { ChevronDown } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

export function FAQ() {

  const faqItems: FAQItem[] = [
    {
      question:
        "Posso convidar outras pessoas para gerenciar minha organização?",
      answer:
        "Sim! Após criar sua organização, você pode enviar convites para membros diretamente pela plataforma. Ao convidar, é possível definir os cargos e as funcionalidades que cada membro terá acesso, de acordo com o plano contratado.",
    },
    {
      question: "Como vejo o desempenho financeiro do meu negócio?",
      answer:
        "No Dashboard Financeiro, acessível pelo menu principal, você pode filtrar os dados por mês e ano. Ele mostra o valor faturado, total recebido, despesas, pagamentos pendentes e detalhamento por formas de pagamento (dinheiro, PIX, cartão). Além disso, há gráficos com os produtos mais vendidos, maiores despesas por categoria, vendas diárias, lucro por produto e uma comparação com o mês anterior.",
    },
    {
      question: "Como identifico pedidos que não foram pagos?",
      answer:
        "O Relatório de Inadimplentes, disponível na tela inicial ou no menu de relatórios, lista todos os pedidos entregues que ainda não foram totalmente pagos, ajudando você a acompanhar os valores pendentes.",
    },
    {
      question: "Como organizo as entregas dos pedidos?",
      answer:
        "O Calendário de Entregas oferece visualizações por mês, semana, dia ou lista, exibindo os pedidos agendados e os que precisam de entrega. Pedidos atrasados aparecem destacados em vermelho, e ao clicar em um item, você vê os detalhes, como o endereço de entrega, com um botão para acessar o pedido completo.",
    },
    {
      question: "Como gerencio os pedidos na plataforma?",
      answer:
        "Na seção de Gestão de Pedidos, os pedidos são exibidos em cards que podem ser filtrados por cliente, tags, status, tipo de entrega, status de pagamento, faixa de valores e datas de preparação. Você também pode cadastrar novos pedidos, selecionando o cliente, produtos, descontos, data de preparação e tipo de entrega (retirada ou entrega com frete).",
    },
    {
      question: "O que são pré-pedidos e como funcionam?",
      answer:
        "Pré-pedidos são pedidos feitos pelos clientes diretamente pelo catálogo online. Na plataforma, você pode visualizá-los com filtros por status (pendente, aceito ou recusado) e, ao aceitá-los, o sistema cria ou atualiza automaticamente o cadastro do cliente e registra o pedido.",
    },
    {
      question: "O que é a funcionalidade de pedidos rápidos?",
      answer:
        "Os Pedidos Rápidos são ideais para vendedores ambulantes, eles permitem registrar pedidos de forma simplificada, informando a data, os produtos e os pagamentos (dinheiro, PIX ou cartão), agilizando o processo no dia a dia.",
    },
    {
      question: "Como cadastro meus clientes?",
      answer:
        "Na seção de Clientes, você pode listar, cadastrar ou editar clientes com informações como nome, CPF/CNPJ, contatos, data de aniversário e múltiplos endereços. Há filtros por nome e tags para facilitar a busca.",
    },
    {
      question: "Como adiciono novos produtos?",
      answer:
        "Na tela de Produtos, você pode visualizar os produtos em grade ou lista e adicionar novos produtos com imagem, nome, tags, descrição, custo unitário e preço de venda. O sistema calcula automaticamente a margem de lucro.",
    },
    {
      question: "Para que servem as tags e como personalizá-las?",
      answer:
        "As Tags são etiquetas personalizáveis que ajudam a organizar clientes, despesas, produtos e pedidos. Você pode definir o nome, a cor da fonte, a cor primária e a cor da borda, e elas aparecem como badges nos cadastros.",
    },
    {
      question: "Como controlo as despesas do meu negócio?",
      answer:
        "Na seção de Despesas, você cadastra despesas com nome, data, tag e formas de pagamento (dinheiro, PIX, cartão). A exibição inicial mostra o mês atual, mas você pode filtrar por nome, período ou tags.",
    },
    {
      question: "Como crio um catálogo online para meus clientes?",
      answer:
        "Na seção de Catálogos, você pode criar um catálogo definindo visibilidade, nome, descrição, valor mínimo do pedido, taxas de entrega, limites de distância e horários de funcionamento. Depois, organize o cardápio em seções com produtos específicos.",
    },
    {
      question: "Como meus clientes acessam o catálogo online?",
      answer:
        "Os clientes acessam o catálogo pelo site catalogos.pedidoz.online, onde podem buscar seu estabelecimento, ver o cardápio completo (com horários, formas de pagamento, pedido mínimo e taxas) e fazer pedidos diretamente.",
    },
    {
      question: "Como os clientes finalizam um pedido pelo catálogo?",
      answer:
        "O cliente insere nome, contato, forma de pagamento, observações e dados de entrega (endereço e CEP). Após confirmar, o pedido é enviado ao sistema, e ele é redirecionado ao WhatsApp com os detalhes para contato com o estabelecimento.",
    },
    {
      question: "Como entro em contato com o suporte da pedidoZ?",
      answer:
        "Nosso time de suporte está disponível de segunda a sábado, das 10h às 18h, para ajudar com qualquer dúvida ou problema. Você pode nos contatar pelo WhatsApp no número 47 98835-5092 ou por e-mail em contato@pedidoz.online.",
    },
  ];

  return (
    <section id="faq" className="py-20 px-4 container mx-auto scroll-mt-24">
      <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
        PERGUNTAS{" "}
        <span className="text-gray-900 dark:text-gray-200 font-black">
          FREQUENTES
        </span>
      </h2>
      <p className="text-white/80 text-center mb-12 text-lg">
        Tire suas dúvidas sobre a plataforma
      </p>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-4xl mx-auto divide-y divide-gray-200 dark:divide-gray-700">
        {faqItems.map((item, index) => (
          <details key={index} className="p-5 group">
            <summary className="flex justify-between items-center cursor-pointer list-none">
              <span className="text-lg font-semibold text-[#121516] dark:text-white">
                {item.question}
              </span>
              <ChevronDown className="w-5 h-5 text-[#EE7B11] transition-transform group-open:rotate-180" />
            </summary>
            <p className="text-[#121516]/80 dark:text-white/80 mt-2">
              {item.answer}
            </p>
          </details>
        ))}
      </div>
    </section>
  );
}
